import { useState, useEffect, useMemo } from "react";

import {
  fetchCities,
  createCity,
  updateCity,
  deleteCity,
  fetchProvinces,
} from "../../../api/admin/coreBasics";

import { swalSuccess, swalError, swalConfirm } from "../../../utils/swalAlert.js";
import EditModal from "../components/EditModal.jsx";

import "../../../styles/AdminPanel/CoreBasic.css"

const EMPTY_FORM = { province: "", name: "" };
const PAGE_SIZE = 8;

export default function Cities() {
  const [cities, setCities] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [page, setPage] = useState(1);

  const load = async () => {
    const c = await fetchCities();
    const p = await fetchProvinces();
    setCities(c.data);
    setProvinces(p.data);
  };

  useEffect(() => {
    load();
  }, []);

  // اگر بعد از حذف/تغییر داده، صفحه‌ی فعلی دیگه موجود نبود، برگرد به صفحه‌ی معتبر
  const totalCount = cities.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const paged = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return cities.slice(start, start + PAGE_SIZE);
  }, [cities, pageSafe]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      province: item.province,
    });
    setIsModalOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.province) {
      swalError({
        title: "اطلاعات ناقص",
        text: "نام شهر و استان الزامی است",
      });
      return;
    }

    const payload = { name: form.name, province: form.province };

    try {
      if (editing) {
        await updateCity(editing.city_id, payload);
      } else {
        await createCity(payload);
      }

      await swalSuccess({ title: "با موفقیت ذخیره شد" });

      setIsModalOpen(false);
      load();
    } catch {
      swalError({ title: "خطا در ذخیره اطلاعات" });
    }
  };

  const remove = async (id) => {
    const confirmed = await swalConfirm({
      title: "حذف شهر؟",
      text: "این عملیات قابل بازگشت نیست",
      confirmText: "بله، حذف شود",
      danger: true,
    });

    if (!confirmed) return;

    try {
      await deleteCity(id);
      await swalSuccess({ title: "شهر حذف شد" });
      load();
    } catch {
      swalError({ title: "خطا در حذف شهر" });
    }
  };

  return (
    <>
      <div className="cities-page">
        <div className="page-header-row">
          <h2 className="page-title">مدیریت شهرها</h2>

          <div className="add-button-wrapper">
            <button className="btn-glass" onClick={openAdd}>
              افزودن شهر
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th>#</th>
                <th>نام شهر</th>
                <th>استان</th>
                <th>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center" }}>
                    داده‌ای وجود ندارد
                  </td>
                </tr>
              ) : (
                paged.map((c, i) => (
                  <tr key={c.city_id}>
                    <td>{(pageSafe - 1) * PAGE_SIZE + i + 1}</td>
                    <td>{c.name}</td>
                    <td>
                      {
                        provinces.find((p) => p.province_id === c.province)
                          ?.name
                      }
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-icon btn-icon-edit"
                        title="ویرایش"
                        onClick={() => openEdit(c)}
                      >
                        <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                      </button>

                      <button
                        className="btn-icon btn-icon-delete"
                        title="حذف"
                        onClick={() => remove(c.city_id)}
                      >
                        <svg viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalCount > 0 && (
            <div className="orders-pagination">
              <span className="pagination-info">
                {totalCount.toLocaleString("fa-IR")} شهر — صفحه{" "}
                {pageSafe.toLocaleString("fa-IR")} از{" "}
                {totalPages.toLocaleString("fa-IR")}
              </span>
              <div className="pagination-controls">
                <button
                  className="page-btn"
                  disabled={pageSafe === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="صفحه قبل"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 6l-6 6 6 6" />
                  </svg>
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    className={`page-btn number${n === pageSafe ? " active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n.toLocaleString("fa-IR")}
                  </button>
                ))}
                <button
                  className="page-btn"
                  disabled={pageSafe === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="صفحه بعد"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {isModalOpen && (
          <EditModal onClose={() => setIsModalOpen(false)}>
            <h3>{editing ? "ویرایش شهر" : "افزودن شهر"}</h3>

            <select
              value={form.province}
              onChange={(e) => setForm({ ...form, province: e.target.value })}
            >
              <option value="">انتخاب استان</option>
              {provinces.map((p) => (
                <option key={p.province_id} value={p.province_id}>
                  {p.name}
                </option>
              ))}
            </select>

            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="نام شهر"
            />

            <div className="modal-buttons">
              <button className="btn" onClick={save}>
                ذخیره
              </button>

              <button
                className="btn btn-cancel"
                onClick={() => setIsModalOpen(false)}
              >
                انصراف
              </button>
            </div>
          </EditModal>
        )}
      </div>
    </>
  );
}
